import React, { useState, useEffect } from 'react';
import { Upload, FileText, Download, Trash2, AlertTriangle, CheckCircle, Clock, File, FileSpreadsheet } from 'lucide-react';
import apiClient from '../../api/client';
import RFQImportModal from './RFQImportModal';
import RFQDocumentsList from './RFQDocumentsList';

const OpportunityDocuments = ({ opportunityId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showRFQImport, setShowRFQImport] = useState(false);
  const [rfqRefreshKey, setRfqRefreshKey] = useState(0);
  const [selectedRFQDocId, setSelectedRFQDocId] = useState(null);

  useEffect(() => {
    if (opportunityId) {
      loadDocuments();
    }
  }, [opportunityId]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/v1/pipeline/opportunities/${opportunityId}/documents`);
      if (response.data.success) {
        setDocuments(response.data.data);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    const file = files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['pdf', 'docx', 'doc', 'xlsx', 'xls'];
    const fileExt = file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExt)) {
      alert(`Unsupported file type. Allowed: ${allowedTypes.join(', ')}`);
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(
        `/api/v1/pipeline/opportunities/${opportunityId}/documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        await loadDocuments();
      } else {
        alert('Failed to upload document');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId, filename) => {
    if (!window.confirm(`Delete ${filename}?`)) return;

    try {
      const response = await apiClient.delete(
        `/api/v1/pipeline/opportunities/${opportunityId}/documents/${documentId}`
      );

      if (response.data.success) {
        await loadDocuments();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  const handleDownload = (documentId, filename) => {
    const url = `/api/v1/pipeline/opportunities/${opportunityId}/documents/${documentId}/download`;
    window.open(apiClient.defaults.baseURL + url, '_blank');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
  };

  const getDocumentIcon = (fileType) => {
    if (fileType === 'PDF') return <FileText className="w-5 h-5 text-red-500" />;
    if (fileType === 'DOCX' || fileType === 'DOC') return <FileText className="w-5 h-5 text-blue-500" />;
    if (fileType === 'XLSX' || fileType === 'XLS') return <FileText className="w-5 h-5 text-green-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const getDocumentTypeBadge = (type) => {
    const colors = {
      'Contract': 'bg-purple-100 text-purple-800 border-purple-200',
      'RFQ': 'bg-blue-100 text-blue-800 border-blue-200',
      'Quote': 'bg-green-100 text-green-800 border-green-200',
      'Invoice': 'bg-orange-100 text-orange-800 border-orange-200',
      'Presentation': 'bg-pink-100 text-pink-800 border-pink-200',
      'Other': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    
    return colors[type] || colors['Other'];
  };

  const getRiskBadgeColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading documents...</div>
      </div>
    );
  }

  const handleRFQImportComplete = () => {
    setRfqRefreshKey(prev => prev + 1);
    loadDocuments();
  };

  return (
    <div className="space-y-4">
      {/* RFQ Import Button */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            setSelectedRFQDocId(null);
            setShowRFQImport(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FileSpreadsheet className="w-4 h-4" />
          Import RFQ
        </button>
      </div>

      {/* RFQ Documents List */}
      <RFQDocumentsList 
        key={rfqRefreshKey}
        opportunityId={opportunityId} 
        onViewDocument={(docId) => {
          setSelectedRFQDocId(docId);
          setShowRFQImport(true);
        }}
      />

      {/* RFQ Import Modal */}
      <RFQImportModal
        isOpen={showRFQImport}
        onClose={() => {
          setShowRFQImport(false);
          setSelectedRFQDocId(null);
        }}
        opportunityId={opportunityId}
        onImportComplete={handleRFQImportComplete}
        existingDocumentId={selectedRFQDocId}
      />

      {/* Upload Zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive
            ? 'border-blue-500 bg-blue-50'
            : uploading
            ? 'border-gray-300 bg-gray-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {uploading ? (
          <div className="space-y-2">
            <Clock className="w-12 h-12 mx-auto text-blue-600 animate-spin" />
            <p className="text-sm text-gray-600">Uploading and analyzing document...</p>
            <p className="text-xs text-gray-500">AI analysis may take 5-10 seconds</p>
          </div>
        ) : (
          <>
            <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            <p className="text-sm font-medium text-gray-700 mb-1">
              Drop files here or click to upload
            </p>
            <p className="text-xs text-gray-500 mb-4">
              PDF, Word, or Excel files (Max 50MB)
            </p>
            <input
              type="file"
              className="hidden"
              id="file-upload"
              accept=".pdf,.docx,.doc,.xlsx,.xls"
              onChange={(e) => handleFileUpload(e.target.files)}
            />
            <label
              htmlFor="file-upload"
              className="inline-block px-4 py-2 bg-blue-600 text-white text-sm rounded cursor-pointer hover:bg-blue-700"
            >
              Choose File
            </label>
          </>
        )}
      </div>

      {/* Documents List */}
      {documents.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-600">No documents uploaded yet</p>
          <p className="text-xs text-gray-500 mt-1">Upload contracts, RFQs, or quotes to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {documents.map((doc) => (
            <div key={doc.id} className="bg-white border border-gray-200 rounded-lg p-4">
              {/* Document Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3 flex-1">
                  {getDocumentIcon(doc.file_type)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-900 truncate">{doc.filename}</h4>
                      {doc.document_type && (
                        <span className={`px-2 py-0.5 text-xs rounded border ${getDocumentTypeBadge(doc.document_type)}`}>
                          {doc.document_type}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(doc.file_size)} • Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDownload(doc.id, doc.filename)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(doc.id, doc.filename)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* AI Analysis */}
              {doc.analysis_status === 'pending' && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600 animate-spin" />
                  <span className="text-sm text-blue-800">AI analysis in progress...</span>
                </div>
              )}

              {doc.analysis_status === 'failed' && (
                <div className="bg-red-50 border border-red-200 rounded p-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-800">AI analysis failed</span>
                </div>
              )}

              {doc.analysis_status === 'completed' && doc.ai_analysis && (
                <div className="bg-gray-50 border border-gray-200 rounded p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-gray-900">AI Analysis</span>
                  </div>

                  {/* Summary */}
                  {doc.ai_analysis.summary && (
                    <div>
                      <p className="text-sm text-gray-700">{doc.ai_analysis.summary}</p>
                    </div>
                  )}

                  {/* Key Terms */}
                  {doc.ai_analysis.key_terms && Object.keys(doc.ai_analysis.key_terms).some(k => doc.ai_analysis.key_terms[k]) && (
                    <div>
                      <h5 className="text-xs font-semibold text-gray-700 mb-2">Key Terms:</h5>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {Object.entries(doc.ai_analysis.key_terms).map(([key, value]) => 
                          value && (
                            <div key={key} className="flex">
                              <span className="text-gray-600 mr-2">{key.replace(/_/g, ' ')}:</span>
                              <span className="font-medium text-gray-900">{value}</span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                  {/* Risk Flags */}
                  {doc.ai_analysis.risk_flags && doc.ai_analysis.risk_flags.length > 0 && (
                    <div>
                      <h5 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Risk Flags:
                      </h5>
                      <div className="space-y-1">
                        {doc.ai_analysis.risk_flags.map((risk, idx) => (
                          <div key={idx} className={`px-2 py-1 rounded text-xs border ${getRiskBadgeColor(risk.severity)}`}>
                            <span className="font-medium uppercase">{risk.severity}:</span> {risk.description}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Important Dates */}
                  {doc.ai_analysis.important_dates && doc.ai_analysis.important_dates.length > 0 && (
                    <div>
                      <h5 className="text-xs font-semibold text-gray-700 mb-2">Important Dates:</h5>
                      <div className="space-y-1">
                        {doc.ai_analysis.important_dates.map((date, idx) => (
                          <div key={idx} className="text-xs text-gray-600">
                            <span className="font-medium">{date.date}:</span> {date.description}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OpportunityDocuments;
